import { computed, ref } from 'vue'

import * as Sentry from '@sentry/vue'
import { jwtDecode, type JwtPayload } from 'jwt-decode'
import { defineStore } from 'pinia'

import { loginApi, logoutApi, refreshTokenApi } from '@/api/users'
import { useLang } from '@/composables/useLang'
import { eModules } from '@/constants'
import { CREATE_NOTIFICATION } from '@/queries/createNotification'
import { DELETE_NOTIFICATION } from '@/queries/deleteNotification'
import { createEntityTypeListPath, GET_ENTITY_TYPES_QUERY } from '@/queries/getEntityTypes'
import { GET_NOTIFICATION_LIST_QUERY } from '@/queries/getNotificationList'
import { GET_TENANT_QUERY } from '@/queries/getTenantInfo'
import { GET_USER_LIST_QUERY } from '@/queries/getUserList'
import { GET_USER_ME_QUERY } from '@/queries/getUserMe'
import { UPDATE_NOTIFICATION } from '@/queries/updateNotification'
import { UPDATE_PASSWORD } from '@/queries/updatePassword'
import { UPLOAD_ENTITY_EDIT_ATTRIBUTE } from '@/queries/uploadEntityEditAttribute'
import { router } from '@/router'
import { useAppStore } from '@/stores/app'
import {
  AllowedLang,
  IEntityType,
  INotification,
  ITenant,
  IUser,
  LangNameAttribute,
} from '@/types/shared'
import apolloClient from '@/utils/apollo'
import {
  cleanLanguages,
  cleanRegion,
  getLanguage,
  getRefreshToken,
  getToken,
  removeToken,
  setLanguage,
  setRefreshToken,
  setToken,
} from '@/utils/cookies'
import request from '@/utils/request'

type ExtendedJwtPayload = JwtPayload & {
  modules: eModules[]
  lang: AllowedLang
  module_permissions: string[]
  maintenance_modes: string[]
  languages: string[]
}

export const useUserStore = defineStore('user', () => {
  // state
  const token = ref(getToken() || '')
  const allowedModules = ref<eModules[]>([])
  const modulePermissions = ref<string[]>([])
  const allowedMaintenanceModes = ref<string[]>([])
  const refreshToken = ref(getRefreshToken() || '')
  const entityTypes = ref<IEntityType[]>([])
  const tenant = ref<ITenant>({} as ITenant)
  const websocketStatus = ref('')
  const keycloakId = ref('')
  const me = ref<IUser>({ id: '' } as IUser)

  // getters
  const regionTypeId = computed(() => {
    const region = entityTypes.value.find(
      entityType => (entityType.attributes as any).system_name === 'region',
    )
    return region ? [region.id] : null
  })

  const hasPerformanceDashboard = computed(() =>
    allowedModules.value.includes(eModules.PERFORMANCE_DASHBOARD),
  )

  const hasPublicApiDashboard = computed(() =>
    allowedModules.value.includes(eModules.PUBLIC_API_DASHBOARD),
  )

  const hasAiModule = computed(() => allowedModules.value.includes(eModules.AI))

  const hasSchedulerPermission = computed(() => modulePermissions.value.includes('scheduler.*'))

  const hasSchedulerComplexQueryPermission = computed(() =>
    modulePermissions.value.includes('scheduler.complex_query'),
  )

  const hasWatcherPermission = computed(() => modulePermissions.value.includes('watcher.*'))

  const hasSettingsPermission = computed(() => modulePermissions.value.length !== 0)

  const hasPerformanceBoardCreatePermission = computed(() => {
    const allowedPermissions = ['performance.board.*', 'performance.board.create']
    return modulePermissions.value.some(element => allowedPermissions.includes(element))
  })

  const hasEntityAttributeEditPermission = computed(() => {
    const allowedPermissions = ['dashboard.entity.*', 'dashboard.entity.manually_editable']
    return modulePermissions.value.some(element => allowedPermissions.includes(element))
  })

  const hasPerformanceBoardDeletePermission = computed(() => {
    const allowedPermissions = ['performance.board.*', 'performance.board.delete']
    return modulePermissions.value.some(element => allowedPermissions.includes(element))
  })

  const hasPerformanceBoardEditPermission = computed(() => {
    const allowedPermissions = ['performance.board.*', 'performance.board.edit']
    return modulePermissions.value.some(element => allowedPermissions.includes(element))
  })

  const hasPublicApiBoardCreatePermission = computed(() => {
    const allowedPermissions = ['public-api.board.*', 'public-api.board.create']
    return modulePermissions.value.some(element => allowedPermissions.includes(element))
  })

  const hasPublicApiBoardDeletePermission = computed(() => {
    const allowedPermissions = ['public-api.board.*', 'public-api.board.delete']
    return modulePermissions.value.some(element => allowedPermissions.includes(element))
  })

  const hasPublicApiBoardEditPermission = computed(() => {
    const allowedPermissions = ['public-api.board.*', 'public-api.board.edit']
    return modulePermissions.value.some(element => allowedPermissions.includes(element))
  })

  const entityTypeOptions = computed(() => {
    const { getValueByLang } = useLang()

    return entityTypes.value.map(entityType => ({
      value: entityType.id,
      label: getValueByLang(entityType.name),
    }))
  })

  const entityTypesWithoutSystem = computed(() => {
    return entityTypes.value.filter(value => value.category !== 'system')
  })

  const entityTypeOptionsWithoutSystem = computed(() => {
    const { getValueByLang } = useLang()

    return entityTypesWithoutSystem.value.map(entityType => ({
      value: entityType.id,
      label: getValueByLang(entityType.name),
    }))
  })

  // actions
  async function login(userInfo: { email: string; password: string }) {
    const { email, password } = userInfo
    const trimmedEmail = email.trim()

    const { data } = await loginApi({ email: trimmedEmail, password })
    if (!data || !data.access_token) {
      throw new Error('Missing token in response')
    }

    const decodedToken = jwtDecode<ExtendedJwtPayload>(data.access_token)
    setToken(data.access_token)
    token.value = data.access_token

    setRefreshToken(data.refresh_token)
    refreshToken.value = data.refresh_token

    const { languages, modules, maintenance_modes, module_permissions, sub } = decodedToken

    keycloakId.value = sub ?? ''
    allowedModules.value = modules
    modulePermissions.value = module_permissions
    allowedMaintenanceModes.value = maintenance_modes

    // Install modules
    const appStore = useAppStore()
    const lang = getLanguage()
    // if (modules.includes('factory-dashboard')) {
    //   const factoryDashboardModule = new FactoryDashboard(router!, store!)
    //   factoryDashboardModule.install()
    // }

    // if (modules.includes('factory-studio')) {
    //   const factoryStudioModule = new FactoryStudio(router!, store!)
    //   factoryStudioModule.install()
    // }

    // if (modules.includes('logs')) {
    //   const logs = new Logs(router!, store!)
    //   logs.install()
    // }

    // if (modules.includes('performance-dashboard')) {
    //   const performanceDashboard = new PerformanceDashboard(router!, store!)
    //   performanceDashboard.install()
    // }

    // if (modules.includes('public-api-dashboard')) {
    //   const publicApiDashboard = new PublicApiDashboard(router!, store!)
    //   publicApiDashboard.install()
    // }

    // CoreModule.install404Router()

    if (lang) {
      appStore.setLanguage(lang)
    }

    if (languages) {
      appStore.setLanguages(languages as unknown as LangNameAttribute)
    }

    return await getMe()
  }

  async function refreshTokenAction(refreshTokenValue: string) {
    try {
      const { data } = await refreshTokenApi({
        refresh_token: refreshTokenValue,
      })
      setToken(data.access_token)
      token.value = data.access_token
      setRefreshToken(data.refresh_token)
      refreshToken.value = data.refresh_token

      return data.access_token
    } catch (e) {
      console.error('Refreshing token', e)
      await logoutApi()
    }
  }

  async function resetToken() {
    return resetAppToDefault()
  }

  async function resetAppToDefault() {
    removeToken()
    cleanRegion()
    cleanLanguages()

    const langTemp = getLanguage()
    localStorage.clear()
    if (langTemp) {
      setLanguage(langTemp)
    }

    me.value = { id: '' } as IUser
    token.value = ''
    refreshToken.value = ''
    allowedModules.value = []
    modulePermissions.value = []
    allowedMaintenanceModes.value = []

    const appStore = useAppStore()
    appStore.setMaintenanceMode('')

    return await apolloClient.clearStore()
  }

  // Add other actions like getMe, getTenantInfo, etc...
  async function setUserInfoFromToken() {
    if (token.value === '') {
      return
    }
    const { languages, lang, modules, module_permissions, maintenance_modes } =
      jwtDecode<ExtendedJwtPayload>(token.value)

    console.log('[setUserInfoFromToken] modules', modules)
    allowedModules.value = modules
    modulePermissions.value = module_permissions
    allowedMaintenanceModes.value = maintenance_modes

    if (
      lang && // if not have local preset of language
      !getLanguage()
    ) {
      useAppStore().setLanguage(lang)
    }

    if (languages) {
      useAppStore().setLanguages(languages as unknown as LangNameAttribute)
    }
    try {
      await getMe()
    } catch (e) {
      console.error('[setUserInfoFromToken] getMe failed:', e)
    }
  }

  async function getTenantInfo() {
    if (token.value === '') {
      throw Error('GetUserInfo: token is undefined!')
    }
    const response = await apolloClient.query({
      query: GET_TENANT_QUERY,
    })
    tenant.value = response.data.tenant

    return response.data.tenant
  }

  async function getEntityTypes(module: string | null = null) {
    const response = await apolloClient.query({
      query: GET_ENTITY_TYPES_QUERY,
      variables: { module: module, pathFunction: createEntityTypeListPath },
    })
    const res: IEntityType[] = response.data.entity_type_list

    entityTypes.value = res

    return res
  }

  async function getUsers() {
    const response = await apolloClient.query({
      query: GET_USER_LIST_QUERY,
      fetchPolicy: 'no-cache',
    })
    const res: IUser[] = response.data.user_list

    return res
  }

  async function getNotificationList() {
    const response = await apolloClient.query({
      query: GET_NOTIFICATION_LIST_QUERY,
      variables: {},
      fetchPolicy: 'no-cache',
    })

    const res: INotification[] = response.data.notification_list

    return res
  }

  async function createNotification(payload: {
    input: {
      user_uuid?: string
      active: boolean
      channels: { sms?: string; mail?: string }
      log_filter: { type: string[]; sources: any[] }
    }
  }) {
    payload.input.user_uuid = me.value.id
    const response = await apolloClient.mutate({
      mutation: CREATE_NOTIFICATION,
      variables: payload,
    })

    return response.data.create_notification as INotification
  }

  async function updateNotification(payload: {
    id: string
    input: {
      user_uuid?: string
      active: boolean
      channels: { sms?: string; mail?: string }
      log_filter: { type: string[]; sources: any[]; lang?: string }
    }
  }) {
    payload.input.user_uuid = me.value.id
    const response = await apolloClient.mutate({
      mutation: UPDATE_NOTIFICATION,
      variables: payload,
    })

    return response.data.update_notification as INotification
  }

  async function deleteNotification(payload: { id: string }) {
    const response = await apolloClient.mutate({
      mutation: DELETE_NOTIFICATION,
      variables: payload,
    })

    return response.data.delete_notificaiton
  }

  async function getMe() {
    if (me.value?.email) {
      return me.value
    }

    const response = await apolloClient.query({
      query: GET_USER_ME_QUERY,
    })
    const res: IUser = response.data.user_me

    me.value = res

    Sentry.setUser({
      id: res.id,
      email: res.email,
      username: res.username,
    })

    return res
  }

  async function updatePassword(payload: { password_old: string; password: string }) {
    const response = await apolloClient.mutate({
      mutation: UPDATE_PASSWORD,
      variables: { input: payload },
    })

    return response.data.update_password.response.error
      ? response.data.update_password.response.error
      : 'success'
  }

  async function logOut() {
    if (token.value === '') {
      throw Error('LogOut: token is undefined!')
    }
    me.value = { id: '' } as IUser
    await logoutApi()
    await resetAppToDefault()
    Sentry.setUser(null)
    await router.replace({ path: '/login' })
    return
  }

  async function setWebsocketStatus(status: string) {
    websocketStatus.value = status
  }

  async function saveEntityEditAttribute(payload: {
    id: string
    vendor: string
    attributes: any
    geom?: any
    ts: string
  }) {
    const response = await apolloClient.mutate({
      mutation: UPLOAD_ENTITY_EDIT_ATTRIBUTE,
      variables: payload,
    })
    return response.data
  }

  async function setNotificationsLanguage(payload: {
    input: {
      language: string
      user_uuid?: string
    }
  }) {
    payload.input.user_uuid = me.value.id

    const response = await request({
      url: '/api/set-notification-language',
      method: 'post',
      data: payload.input,
    })

    return response.data
  }

  async function getNotificationsLanguage() {
    const response = await request({
      url: '/api/notifications-language',
      method: 'get',
    })
    return response.data
  }

  return {
    // state
    token,
    allowedModules,
    modulePermissions,
    allowedMaintenanceModes,
    refreshToken,
    entityTypes,
    tenant,
    websocketStatus,
    keycloakId,
    me,
    // getters
    regionTypeId,
    hasPerformanceDashboard,
    hasPublicApiDashboard,
    hasAiModule,
    hasSchedulerPermission,
    hasSchedulerComplexQueryPermission,
    hasWatcherPermission,
    hasSettingsPermission,
    hasEntityAttributeEditPermission,
    hasPerformanceBoardCreatePermission,
    hasPerformanceBoardDeletePermission,
    hasPerformanceBoardEditPermission,
    hasPublicApiBoardCreatePermission,
    hasPublicApiBoardDeletePermission,
    hasPublicApiBoardEditPermission,
    entityTypeOptions,
    entityTypesWithoutSystem,
    entityTypeOptionsWithoutSystem,
    // actions
    login,
    refreshTokenAction,
    resetAppToDefault,
    setUserInfoFromToken,
    getTenantInfo,
    getEntityTypes,
    getUsers,
    getNotificationList,
    createNotification,
    updateNotification,
    deleteNotification,
    resetToken,
    getMe,
    updatePassword,
    logOut,
    setWebsocketStatus,
    saveEntityEditAttribute,
    setNotificationsLanguage,
    getNotificationsLanguage,
  }
})
